import { ArrowRightIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import type { FlightOfferFlight } from "@/features/flights/types/types";

type FlightFaresPageHeaderProps = {
  header: string;
  flight: FlightOfferFlight;
  stepper?:
    | {
        currentStep: number;
        totalSteps: number;
      }
    | false;
};

export function FlightFaresPageHeader({
  header,
  flight,
  stepper,
}: FlightFaresPageHeaderProps) {
  const intl = useIntl();

  const { arrivalTime, departureTime, arrivalCode, departureCode } =
    useMemo(() => {
      const firstSegment = flight.segments[0];
      const lastSegment = flight.segments[flight.segments.length - 1];

      const departureCode = firstSegment.departure.airport.code;
      const arrivalCode = lastSegment.arrival.airport.code;

      const departureTime = firstSegment.departure.time;
      const arrivalTime = lastSegment.arrival.time;

      return {
        departureCode,
        arrivalCode,
        departureTime,
        arrivalTime,
      };
    }, [flight]);

  return (
    <div
      className={clsx("flex items-center justify-between gap-2", {
        "justify-start": !stepper,
      })}
    >
      <div className="flex flex-col font-medium text-text-primary">
        <h1 className="text-xl">{header}</h1>

        <p className="flex items-center gap-1 text-sm">
          {departureTime} - {arrivalTime}
          <span className="size-1 rounded-full bg-stroke-neutral-medium" />
          {departureCode}
          <ArrowRightIcon className="size-3 rtl:-scale-x-100" />
          {arrivalCode}
        </p>
      </div>

      {!!stepper && (
        <p className="rounded-lg bg-surface-accent-soft px-2 py-1 text-xs font-medium text-text-primary">
          {intl.formatMessage(
            {
              defaultMessage: "Step {currentStep} of {totalSteps}",
              description:
                "Header for the selected departure flight in the fare step",
            },
            {
              currentStep: stepper.currentStep,
              totalSteps: stepper.totalSteps,
            },
          )}
        </p>
      )}
    </div>
  );
}
